import localForage from 'localforage';
import { JsDriver } from './JsDriver';

//#region Interfaces
export type IBrowserStorage = LocalForageDbMethodsCore;
export type IStorageObject = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: (key: string) => Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set: (key: string, value: any) => Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  remove: (key: string) => Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clear: () => Promise<any>;
};
//#endregion

localForage.defineDriver(JsDriver);
localForage.config({
  driver: [
    localForage.INDEXEDDB,
    localForage.WEBSQL,
    localForage.LOCALSTORAGE,
    'DoryJsDriver',
  ],
});

export class BrowserStorage {
  private storageObjects = new Map<string, IStorageObject>();

  /**
   * creates a namespaced storageObject
   *
   * @param namespace
   * @param storage
   */
  public createStorageObject(
    namespace: string,
    storageType = 'localStorage'
  ): IStorageObject {
    let storageContainer: IBrowserStorage;
    if (this.storageObjects.has(namespace)) {
      throw new Error(
        `Could not create browser storage object - namespace ${namespace} already exists`
      );
    }

    // translate storage string into a real storage object
    switch (storageType) {
      case 'local':
      case 'localStorage':
        storageContainer = localForage;
        break;
      default:
        throw new TypeError('invalid storage location string');
    }

    const get = (key: string): Promise<unknown> => {
      return new Promise((resolve, reject) => {
        storageContainer.getItem(
          `${namespace}.${key}`,
          (err, result: string) => {
            if (err) {
              reject(err);
            } else {
              resolve(JSON.parse(result));
            }
          }
        );
      });
    };

    const set = (key: string, value: unknown): Promise<unknown> => {
      return new Promise((resolve, reject) => {
        storageContainer.setItem(
          `${namespace}.${key}`,
          JSON.stringify(value),
          (err: string) => {
            if (err) {
              reject(err);
            } else {
              resolve(null);
            }
          }
        );
      });
    };

    const remove = (key: string): Promise<unknown> => {
      return new Promise((resolve, reject) => {
        storageContainer.removeItem(`${namespace}.${key}`, (err: string) => {
          if (err) {
            reject(err);
          } else {
            resolve(null);
          }
        });
      });
    };

    const clear = async (): Promise<unknown> => {
      const toBeRemoved = [];
      const numberOfKeys = await storageContainer.length();
      for (let i = 0; i < numberOfKeys; i++) {
        const key = await storageContainer.key(i);
        if (key.startsWith(`${namespace}.`)) {
          toBeRemoved.push(key);
        }
      }
      return Promise.all(
        toBeRemoved.map((key) => storageContainer.removeItem(key))
      );
    };

    const storageObject: IStorageObject = {
      get,
      set,
      remove,
      clear,
    };
    this.storageObjects.set(namespace, storageObject);
    return storageObject;
  }

  /**
   * get StorageObject for a provided namespace
   *
   * @param namespace
   */
  public getStorageObject(namespace: string): IStorageObject {
    return this.storageObjects.get(`${namespace}`);
  }
}
export default new BrowserStorage();
